/**
 * /e/$entity/$id — the record as a PAGE.
 *
 * The record's body (identity, form, write-door notice, save handling) is
 * `record/RecordBody`, shared with the pane beside the list — see its header for
 * why there is one of it and not two. What stays here is what is genuinely
 * page-only: the BREADCRUMB TRAIL, and the two relationship surfaces that need
 * page width.
 *
 * ── The trail (2026-08-06) ─────────────────────────────────────────────────
 * Christopher: org → People (child grid) → contact → back landed on GENERAL
 * contacts, not the organization he came through; and nothing depicted the path
 * at all. The crumbs come from `record/trail.ts`, written by whoever navigated
 * here THROUGH a relationship; with no trail (a direct open, a pasted link) the
 * crumb row degrades to the entity's own list — the old behaviour, now as the
 * fallback instead of the only answer. Back = the LAST crumb, so it retraces
 * one hop rather than teleporting to a list you were never on.
 *
 * ── Master left, related right (2026-08-07) ────────────────────────────────
 * `RecordBody` owns that two-column body, so the page and the split pane get
 * the record's shape from one place. This route used to render
 * `RecordRelations` itself, BELOW the card — which made a record look different
 * depending on where you opened it.
 *
 * The party panel is a RICHER READING of relationships core already registers —
 * not a different source of truth. It stays for the two party entities; the
 * registry-derived `RecordRelations` is what every entity gets (#484).
 */
import { useLocation, useNavigate, useParams } from '@tanstack/react-router'
import { Surface } from '@solum/ui'
import { singularize } from '@solum/experience'
import type { Row } from '@solum/sdk'
import { useEntityIdentities, useEntityRow, useSchema } from '../../data/queries'
import { PartyRelations } from '../PartyRelations'
import { PARTY_ENTITIES } from '../../record/partyEntities'
import { RecordBody, headingFor } from '../../record/RecordBody'
import { recordAncestors, extendTrail, type Crumb } from '../../record/trail'
import { useDeclareRouteChrome } from '../routeChrome'
import { recordDestination } from '../recordDestination'
import s from './EntityDetail.module.css'


export function EntityDetail() {
  const { entity, id } = useParams({ from: '/e/$entity/$id' })
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const schema = useSchema()
  const identities = useEntityIdentities()
  const contract = (identities.data ?? []).find((item) => item.entity_type === entity)
  const navigationEligible = contract?.navigation_eligible === true
  const entityLabel = ((schema.data?.entities ?? []) as { key: string; label: string }[])
    .find((e) => e.key === entity)?.label ?? entity
  // The record's own name, for the crumb row and the relationship panels below.
  // `useEntityRow` is the same cached read `RecordBody` makes.
  const rowq = useEntityRow(entity, id, navigationEligible)
  const heading = headingFor((rowq.data ?? {}) as Row, singularize(entityLabel), id === 'new')

  /* The path that led here — or the entity list, when nothing did. */
  const crumbs: Crumb[] = recordAncestors(pathname, { label: entityLabel, to: `/e/${entity}` })
  const back = crumbs[crumbs.length - 1]!

  /* …declared to the header, and withdrawn when this route unmounts (#2349 §A).
     Unconditionally, and above the early returns: a hook below one is a blank
     page rather than an error, and the two refusal branches below are still
     this route — a header describing the previous record over an "unreadable
     contract" panel is exactly the stale chrome the slot's own contract
     forbids. */
  useDeclareRouteChrome({ back, crumbs })

  if (identities.isPending) {
    return <div className={s.page}><Surface className={s.card} role="status">Loading record contract…</Surface></div>
  }
  if (identities.isError || !navigationEligible) {
    return (
      <div className={s.page}>
        <Surface className={s.card} role="alert">
          {identities.isError
            ? 'The record contract could not be read.'
            : 'This surface is not a navigable record.'}
        </Surface>
      </div>
    )
  }

  /* Opening a RELATED record continues the journey: the trail that led here,
     plus this record, written for the target — see trail.ts. */
  const openRelated = (targetEntity: string, rid: string) => {
    /* #2279 fixed the SAME defect this line did — a related open kept the
       parent's entity — and its fix is preserved rather than reverted: the
       target entity travels. This version also asks `recordDestination`, so an
       opportunity still reaches the CRM desk peek (the ledger's RETAIN row)
       instead of the canonical route, and the trail is only extended for a
       destination that IS the canonical route. */
    const destination = recordDestination(targetEntity, rid)
    if (destination.to === '/e/$entity/$id') {
      const target = `/e/${targetEntity}/${rid}`
      extendTrail(pathname, { label: heading, to: pathname }, target)
    }
    void navigate(destination)
  }

  return (
    <div className={s.page}>
      {/* No crumb row HERE any more (#2349 §A, mock item 6): Back and the path
          are declared to the header's route-chrome slot above. The model is
          unchanged — the same `recordAncestors` reading of the same
          `record/trail.ts` journey — so what moved is where it is drawn, and
          the record page gets its top row back for the record. */}
      {/* The h1 is the RECORD BODY's — with the record's ⌘ beside it. It is the
          one visible occurrence of the current record title; the chrome above
          names ancestors only. */}
      {/* Master left, related right — the same invariant the pane follows. The
          page used to stack them, which made the record's shape depend on where
          you opened it (Christopher, 2026-08-07: "Related should always be on
          the right (as default, on any detail form)"). `RecordBody` owns the
          two-column body now, so both surfaces get it from one place. */}
      {/* NO PLATE AROUND THE RECORD (#2406, leading design). A `<Surface>` used
          to wrap the whole record, so the header, the form and the relations
          were three regions of one white card. The design seats them on the
          ground — the header as its own dark slab, then Details and
          Connections as two slabs — and `RecordShell`'s page presentation now
          draws those, so a plate here would put cards on a card. */}
        <RecordBody
          entity={entity} id={id} presentation="page" headingTestid="record-heading"
          relations={id !== 'new'}
          onOpenRelated={(targetEntity, rid) => openRelated(targetEntity, rid)}
          onSaved={({ id: savedId, intent, created }) => {
            if (intent === 'close') {
              return navigate({ to: '/e/$entity', params: { entity } })
            }
            if (created) {
              return navigate({
                to: '/e/$entity/$id', params: { entity, id: savedId }, replace: true,
              })
            }
          }}
        />
      {id !== 'new' && PARTY_ENTITIES.has(entity) && <PartyRelations entity={entity} id={id} />}
    </div>
  )
}
